import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as dashboardActions from "src/redux/slices/dashboardSlice";
import TableV2 from "src/components/ui/TableV2";
import { formatDateTime } from "src/utils/date-time";
import { currencyFormatter } from "src/utils/common";
import { ITenantRecentPurchaseOrder } from "src/types/dashboard.types";

interface RecentPurchaseCardProps {
  limit?: number;
  enabled?: boolean;
  warehouseId?: string;
}

const RecentPurchaseCard: React.FC<RecentPurchaseCardProps> = ({
  limit = 5,
  enabled = true,
  warehouseId,
}) => {
  const dispatch = useAppDispatch();
  const { tenantRecentPurchases } = useAppSelector((state) => state.dashboard);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";

  useEffect(() => {
    if (!enabled) return;
    const params = {
      limit,
      ...(isTenantAdmin || !user?._id ? {} : { userId: user._id }),
      ...(warehouseId ? { warehouseId } : {}),
    };
    dispatch(dashboardActions.getTenantRecentPurchasesRequest(params));
  }, [dispatch, enabled, isTenantAdmin, limit, user?._id, warehouseId]);

  if (!enabled) return null;

  const rows: ITenantRecentPurchaseOrder[] =
    tenantRecentPurchases.data?.data || [];

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          {isTenantAdmin ? "Recent Purchase Orders" : "My Recent Purchase Orders"}
        </h2>
        <span className="text-xs text-gray-400 dark:text-gray-500">Latest {limit}</span>
      </div>
      <TableV2
        columns={[
          {
            key: "purchaseOrderId",
            header: "Order",
            accessor: ({ row }: { row: ITenantRecentPurchaseOrder }) => (
              <Link to={`/app/purchase/${row._id}`} className="text-primary-600">
                {row.purchaseOrderId}
              </Link>
            ),
          },
          {
            key: "supplier",
            header: "Supplier",
            accessor: ({ row }: { row: ITenantRecentPurchaseOrder }) =>
              row.supplierId?.name || "-",
          },
          {
            key: "grandTotal",
            header: "Total",
            align: "right",
            accessor: ({ row }: { row: ITenantRecentPurchaseOrder }) =>
              currencyFormatter(row.grandTotal || 0),
          },
          {
            key: "createdAt",
            header: "Date",
            align: "center",
            accessor: ({ row }: { row: ITenantRecentPurchaseOrder }) =>
              formatDateTime(row.createdAt, "DD/MM/YYYY hh:mm:ss A"),
          },
        ]}
        data={rows}
        loading={tenantRecentPurchases.loading}
      />
    </div>
  );
};

export default RecentPurchaseCard;
